
import { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

function useSectionReveal() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

type SectionRevealProps = {
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div';
  [key: string]: unknown;
};

function SectionReveal({
  children,
  className = '',
  as = 'section',
  ...props
}: SectionRevealProps) {
  const ref = useSectionReveal();
  const Tag = as;
  return (
    <Tag ref={ref} className={`section-reveal ${className}`} {...props}>
      {children}
    </Tag>
  );
}

interface ProgramCardProps {
  name: string;
  applications: string;
  features: string[];
  priceRange: string;
  highlight?: boolean;
  badge?: string;
}

function ProgramCard({ name, applications, features, priceRange, highlight, badge }: ProgramCardProps) {
  return (
    <div className={`relative rounded-lg p-6 h-full flex flex-col ${highlight ? 'bg-emerald-800 text-white ring-2 ring-emerald-600' : 'bg-white border border-gray-200'}`}>
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
            {badge}
          </span>
        </div>
      )}
      <div className="mb-4">
        <h4 className={`text-xl font-bold mb-1 ${highlight ? 'text-white' : 'text-gray-900'}`}>{name}</h4>
        <p className={`text-sm ${highlight ? 'text-emerald-200' : 'text-gray-500'}`}>{applications}</p>
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <div className={`w-4 h-4 flex items-center justify-center shrink-0 mt-0.5 ${highlight ? 'text-emerald-300' : 'text-emerald-600'}`}>
              <i className="ri-check-line"></i>
            </div>
            <span className={highlight ? 'text-emerald-50' : 'text-gray-700'}>{feature}</span>
          </li>
        ))}
      </ul>
      <div className={`pt-4 border-t ${highlight ? 'border-emerald-700' : 'border-gray-100'}`}>
        <div className={`text-xs uppercase tracking-wide mb-1 ${highlight ? 'text-emerald-300' : 'text-gray-500'}`}>
          Per Acre
        </div>
        <div className={`text-2xl font-bold ${highlight ? 'text-white' : 'text-emerald-800'}`}>
          {priceRange}
        </div>
      </div>
    </div>
  );
}

interface CropSectionProps {
  title: string;
  icon: string;
  description: string;
  programs: {
    core: { priceRange: string };
    priority: { priceRange: string };
    fullSeason: { priceRange: string };
  };
}

function CropSection({ title, icon, description, programs }: CropSectionProps) {
  const [activeTab, setActiveTab] = useState<'core' | 'priority' | 'fullSeason'>('priority');

  const coreFeatures = [
    '1–2 planned applications',
    'Standard scheduling',
    'Precision aerial application',
    'Farmer supplies chemistry',
    'Post-application summary',
  ];

  const priorityFeatures = [
    '2–3 planned applications',
    'Priority scheduling during tight windows',
    'Locked seasonal pricing',
    'Proactive coordination',
    'Farmer supplies chemistry',
    'Dedicated account contact',
  ];

  const fullSeasonFeatures = [
    '3+ planned applications',
    'Guaranteed execution windows',
    'Best per-acre pricing',
    'Highest scheduling priority',
    'Farmer supplies chemistry',
    'Season planning consultation',
    'Priority rebooking for next season',
  ];

  return (
    <div className="bg-stone-50 rounded-xl p-8 md:p-10">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-lg shrink-0">
          <i className={`${icon} text-3xl text-emerald-700`}></i>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 leading-relaxed">{description}</p>
        </div>
      </div>

      {/* Mobile Tab Selector */}
      <div className="md:hidden mb-6">
        <div className="bg-white rounded-full p-1 flex gap-1 border border-gray-200">
          <button
            onClick={() => setActiveTab('core')}
            className={`flex-1 py-2 px-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === 'core' ? 'bg-emerald-800 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Core
          </button>
          <button
            onClick={() => setActiveTab('priority')}
            className={`flex-1 py-2 px-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === 'priority' ? 'bg-emerald-800 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Priority
          </button>
          <button
            onClick={() => setActiveTab('fullSeason')}
            className={`flex-1 py-2 px-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === 'fullSeason' ? 'bg-emerald-800 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Full Season
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden">
        {activeTab === 'core' && (
          <ProgramCard
            name="Core Program"
            applications="1–2 planned applications"
            features={coreFeatures}
            priceRange={programs.core.priceRange}
          />
        )}
        {activeTab === 'priority' && (
          <ProgramCard
            name="Priority Program"
            applications="2–3 planned applications"
            features={priorityFeatures}
            priceRange={programs.priority.priceRange}
            highlight
            badge="Most Popular"
          />
        )}
        {activeTab === 'fullSeason' && (
          <ProgramCard
            name="Full Season Program"
            applications="3+ planned applications"
            features={fullSeasonFeatures}
            priceRange={programs.fullSeason.priceRange}
            badge="Best Value"
          />
        )}
      </div>

      {/* Desktop Grid */}
      <div className="hidden md:grid md:grid-cols-3 gap-6">
        <ProgramCard
          name="Core Program"
          applications="1–2 planned applications"
          features={coreFeatures}
          priceRange={programs.core.priceRange}
        />
        <ProgramCard
          name="Priority Program"
          applications="2–3 planned applications"
          features={priorityFeatures}
          priceRange={programs.priority.priceRange}
          highlight
          badge="Most Popular"
        />
        <ProgramCard
          name="Full Season Program"
          applications="3+ planned applications"
          features={fullSeasonFeatures}
          priceRange={programs.fullSeason.priceRange}
          badge="Best Value"
        />
      </div>
    </div>
  );
}

export default function SeasonalProgramsPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Seasonal Application Programs - Greenfield Partners',
    description: 'Program-based pricing for precision aerial application services. Predictable costs, priority scheduling, and better execution through planning.',
    provider: {
      '@type': 'Organization',
      name: 'Greenfield Partners',
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Seasonal Application Programs - Precision Aerial Application Pricing | Greenfield Partners"
        description="Program-based pricing for precision aerial application. Predictable costs, priority scheduling, and better execution through planning. Application services only - farmer supplies chemistry."
        keywords="aerial application pricing, drone spraying programs, seasonal spray programs, precision agriculture pricing, crop spraying costs"
        schema={schema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9de386b28ac9cc08a9c3ff50b358a185.jpg"
            alt="Precision aerial application over agricultural fields"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/80 via-emerald-900/60 to-emerald-950/70"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="inline-block text-emerald-400 font-bold text-xs tracking-widest uppercase mb-4">
              Program-Based Pricing
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-wide">
              Seasonal Application Programs
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-6 font-light">
              Predictable pricing. Priority scheduling. Better execution through planning. Our seasonal programs give you locked-in rates and guaranteed access during critical spray windows.
            </p>
            <div className="flex items-center gap-3 text-emerald-300 text-sm font-medium">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-information-line text-lg"></i>
              </div>
              <span>Application services only — farmer supplies chemistry</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Programs Section */}
      <SectionReveal className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Why Choose a Program
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-wide">
              Plan Ahead. Stress Less.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Seasonal programs aren&apos;t just about saving money — they&apos;re about reducing uncertainty during the most critical weeks of your growing season.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-stone-50 rounded-lg p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-lock-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Locked Pricing</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                No surprises. Your per-acre rate is set at the start of the season.
              </p>
            </div>

            <div className="bg-stone-50 rounded-lg p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Priority Access</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Program members get scheduled first during tight spray windows.
              </p>
            </div>

            <div className="bg-stone-50 rounded-lg p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-team-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Proactive Coordination</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                We reach out before windows open — not after they close.
              </p>
            </div>

            <div className="bg-stone-50 rounded-lg p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Execution Guarantee</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Full Season members get guaranteed windows — weather permitting.
              </p>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* Row Crops Section */}
      <SectionReveal className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <CropSection
            title="Row Crops"
            icon="ri-plant-line"
            description="Corn, soybeans, wheat, and other field crops. Ideal for fungicide timing, foliar nutrients, and late-season applications when ground equipment causes damage."
            programs={{
              core: { priceRange: '$26–30' },
              priority: { priceRange: '$22–26' },
              fullSeason: { priceRange: '$20–23' },
            }}
          />
        </div>
      </SectionReveal>

      {/* Specialty Crops Section */}
      <SectionReveal className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <CropSection
            title="Specialty Crops"
            icon="ri-seedling-line"
            description="Vegetables, hemp, tobacco, and high-value crops requiring precise application and careful timing. Often involves tighter spray windows and more frequent passes."
            programs={{
              core: { priceRange: '$30–35' },
              priority: { priceRange: '$26–30' },
              fullSeason: { priceRange: '$23–27' },
            }}
          />
        </div>
      </SectionReveal>

      {/* Tree Farms Section */}
      <SectionReveal className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <CropSection
            title="Tree Farms & Perennials"
            icon="ri-tree-line"
            description="Orchards, vineyards, Christmas trees, and perennial plantings. Requires specialized flight patterns and often involves more complex terrain navigation."
            programs={{
              core: { priceRange: '$32–38' },
              priority: { priceRange: '$28–34' },
              fullSeason: { priceRange: '$25–30' },
            }}
          />
        </div>
      </SectionReveal>

      {/* How Pricing Is Adjusted */}
      <SectionReveal className="py-20 px-6 bg-stone-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Pricing Factors
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-wide">
              How Pricing Is Adjusted
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              The ranges above are typical starting points. Final pricing is confirmed after field verification and depends on several factors. Not sure about your field complexity? <a href="/field-complexity" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Use our estimator tool <i className="ri-arrow-right-line"></i></a>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg mb-4">
                <i className="ri-landscape-line text-2xl text-amber-700"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Field Complexity</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Terrain, obstacles, and field shape affect flight time and efficiency. <a href="/field-complexity" className="text-emerald-800 hover:text-emerald-700 font-medium">Estimate your field complexity</a> to understand pricing factors.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Trees, power lines, or structures within fields</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Hilly terrain requiring altitude adjustments</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Irregular shapes with sharp turns</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Multiple small fields vs. contiguous blocks</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg mb-4">
                <i className="ri-calendar-event-line text-2xl text-red-700"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Peak Demand Windows</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Timing affects availability and scheduling priority. Check our <a href="/spray-window-advisor" className="text-emerald-800 hover:text-emerald-700 font-medium">Spray Window Advisor</a> and <a href="/crop-calendar" className="text-emerald-800 hover:text-emerald-700 font-medium">Crop Calendar</a> for optimal timing.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-0.5"></i>
                  <span>R3 soybean fungicide window (July–August)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-0.5"></i>
                  <span>VT/R1 corn tassel applications</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-0.5"></i>
                  <span>Post-rain recovery windows</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-0.5"></i>
                  <span>Program members get priority during peaks</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg mb-4">
                <i className="ri-stack-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Acreage Tier-Downs</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Larger operations benefit from volume efficiencies.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>250+ acres: First tier discount</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>500+ acres: Additional reduction</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>1,000+ acres: Best volume pricing</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>Multi-farm coordination available</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* Application Only Callout */}
      <SectionReveal className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                <i className="ri-flask-line text-2xl text-amber-700"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Application Services Only</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All pricing covers aerial application services only. <strong>Farmers supply their own chemistry.</strong> This keeps costs transparent and lets you work with your preferred agronomist or retailer for product selection.
                </p>
                <ul className="grid md:grid-cols-2 gap-3 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-600"></i>
                    <span>Fungicides</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-600"></i>
                    <span>Herbicides</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-600"></i>
                    <span>Foliar nutrients</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-600"></i>
                    <span>Plant growth regulators</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-600"></i>
                    <span>Insecticides</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-600"></i>
                    <span>Cover crop seed</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* Comparison Table */}
      <SectionReveal className="py-20 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Quick Comparison
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-wide">
              Program Comparison at a Glance
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-100">
                    <th className="text-left py-4 px-6 font-bold text-gray-900">Feature</th>
                    <th className="text-center py-4 px-6 font-bold text-gray-900">Core</th>
                    <th className="text-center py-4 px-6 font-bold text-emerald-800 bg-emerald-50">Priority</th>
                    <th className="text-center py-4 px-6 font-bold text-gray-900">Full Season</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Planned Applications</td>
                    <td className="py-4 px-6 text-center text-gray-600">1–2</td>
                    <td className="py-4 px-6 text-center text-emerald-800 bg-emerald-50/50 font-medium">2–3</td>
                    <td className="py-4 px-6 text-center text-gray-600">3+</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Scheduling Priority</td>
                    <td className="py-4 px-6 text-center text-gray-600">Standard</td>
                    <td className="py-4 px-6 text-center text-emerald-800 bg-emerald-50/50 font-medium">Priority</td>
                    <td className="py-4 px-6 text-center text-gray-600">Highest</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Locked Seasonal Pricing</td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center bg-emerald-50/50">
                      <i className="ri-check-line text-emerald-600 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-check-line text-emerald-600 text-lg"></i>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Proactive Coordination</td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center bg-emerald-50/50">
                      <i className="ri-check-line text-emerald-600 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-check-line text-emerald-600 text-lg"></i>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Guaranteed Execution Windows</td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center bg-emerald-50/50">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-check-line text-emerald-600 text-lg"></i>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Season Planning Consultation</td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center bg-emerald-50/50">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-check-line text-emerald-600 text-lg"></i>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Priority Rebooking</td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center bg-emerald-50/50">
                      <i className="ri-close-line text-gray-400 text-lg"></i>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <i className="ri-check-line text-emerald-600 text-lg"></i>
                    </td>
                  </tr>
                  <tr className="bg-stone-50">
                    <td className="py-4 px-6 font-bold text-gray-900">Row Crops (per acre)</td>
                    <td className="py-4 px-6 text-center font-bold text-gray-700">$26–30</td>
                    <td className="py-4 px-6 text-center font-bold text-emerald-800 bg-emerald-50">$22–26</td>
                    <td className="py-4 px-6 text-center font-bold text-gray-700">$20–23</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* Disclaimer */}
      <SectionReveal className="py-12 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center text-gray-500 mt-0.5 shrink-0">
                <i className="ri-information-line text-lg"></i>
              </div>
              <div className="text-sm text-gray-600 leading-relaxed">
                <strong className="text-gray-800">Pricing Disclaimer:</strong> All pricing shown is for application services only and represents typical ranges for standard field conditions. Final pricing is confirmed after field verification and may vary based on field complexity, terrain, obstacles, and scheduling requirements. Chemistry and products are supplied by the farmer. Weather conditions may affect scheduling and execution windows.
              </div>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-emerald-900 topo-texture-dark">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-wide">
            Ready to Plan Your Season?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 leading-relaxed font-light max-w-2xl mx-auto">
            Let&apos;s talk about your fields, your timing, and which program makes sense for your operation. No pressure — just a practical conversation. Want a quick estimate first? <a href="/instant-quote" className="text-white hover:text-emerald-200 font-semibold underline">Get an instant quote</a>.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white text-emerald-900 font-bold px-8 py-4 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-chat-3-line"></i>
              Start a Conversation
            </a>
            <a
              href="tel:+17179131780"
              className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-bold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              717-913-1780
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
